import React, { useState, useCallback, useMemo } from 'react'
import { useReflection } from './hooks/useReflection.js'
import { useAutoSave } from '../hooks/useAutoSave.js'
import { useGoals } from '../goals/hooks/useGoals.js'
import { suggestManagerNote } from '../api/claude.js'
import { ReflectionLifecycleBar } from './ReflectionLifecycleBar.jsx'
import { ReflectionGoalsStrip } from './ReflectionGoalsStrip.jsx'
import { SuccessesTrack } from './tracks/SuccessesTrack.jsx'
import { ChallengesTrack } from './tracks/ChallengesTrack.jsx'
import { SideQuestsTrack } from './tracks/SideQuestsTrack.jsx'
import { ForwardLookSection } from './forwardLook/ForwardLookSection.jsx'
import { ReflectionPrivateNotes } from './privateNotes/ReflectionPrivateNotes.jsx'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function ReflectionDetailPage({
  reflectionId,
  me,
  ownerUserId = null,
  onReflectionChanged,
  reports = [],
}) {
  const effectiveOwner = ownerUserId || me?.userId
  const {
    reflection, loading,
    updateReflection, markReady,
    shareReflection, closeReflection, reopenReflection,
    promoteCommitment,
  } = useReflection({ reflectionId, ownerUserId: effectiveOwner })

  // Load goals for sub-goal pickers
  const { goals } = useGoals({
    ownerUserId: effectiveOwner,
    enabled: !!effectiveOwner && !reflection?.closedAt,
  })

  const isEmployee = me?.userId === reflection?.ownerUserId
  const isManager = !isEmployee && !!reflection
  const isClosed = !!reflection?.closedAt
  const isReady = !!reflection?.readyAt
  const isShared = !!reflection?.sharedAt
  const hasManager = !!reflection?.managerId

  // ── Autosave: employee content draft ──
  const [employeeDraft, setEmployeeDraft] = useState(null)
  const [managerDraft, setManagerDraft] = useState(null)

  const handleEmployeeSave = useCallback(async (_id, data) => {
    await updateReflection(data)
    setEmployeeDraft(null)
  }, [updateReflection])

  const handleManagerSave = useCallback(async (_id, data) => {
    await updateReflection(data)
    setManagerDraft(null)
  }, [updateReflection])

  const employeeSaveState = useAutoSave(
    reflectionId,
    employeeDraft,
    handleEmployeeSave,
    isEmployee && !isClosed && employeeDraft !== null,
  )

  const managerSaveState = useAutoSave(
    `${reflectionId}-mgr`,
    managerDraft,
    handleManagerSave,
    isManager && !isClosed && isReady && managerDraft !== null,
  )

  // ── Flatten all subgoals from goals ──
  const allSubgoals = useMemo(() => {
    if (!goals) return []
    return goals
      .filter((g) => g.status === 'active')
      .flatMap((g) => (g.subgoals || []).map((sg) => ({ ...sg, goalId: g.goalId })))
  }, [goals])

  // ── Employee content change handlers ──
  function handleTrackChange(trackKey, data) {
    setEmployeeDraft((prev) => ({
      ...(prev || {}),
      [trackKey]: data,
    }))
  }

  function handleForwardLookChange(data) {
    setEmployeeDraft((prev) => ({
      ...(prev || {}),
      forwardLook: data,
    }))
  }

  // ── Manager note change handlers ──
  function handleManagerNoteChange(sectionKey, notes) {
    setManagerDraft((prev) => {
      const currentNotes = prev?.managerNotes || reflection?.managerNotes || {}
      return {
        ...(prev || {}),
        managerNotes: { ...currentNotes, [sectionKey]: notes },
      }
    })
  }

  // ── Lifecycle actions ──
  async function handleMarkReady() {
    await markReady()
    onReflectionChanged?.()
  }

  async function handleShare() {
    await shareReflection()
    onReflectionChanged?.()
  }

  async function handleClose() {
    await closeReflection()
    onReflectionChanged?.()
  }

  async function handleReopen() {
    await reopenReflection()
    onReflectionChanged?.()
  }

  async function handlePromoteToTask(commitmentId, task) {
    await promoteCommitment(commitmentId, {
      title: task.title,
      pairId: task.pairId,
    })
  }

  // ── Resolve current track data (draft or server) ──
  function getTrackData(key) {
    if (employeeDraft?.[key]) return employeeDraft[key]
    return reflection?.[key] || {}
  }

  function getManagerNotes(sectionKey) {
    if (managerDraft?.managerNotes?.[sectionKey]) return managerDraft.managerNotes[sectionKey]
    return reflection?.managerNotes?.[sectionKey] || {}
  }

  function getForwardLook() {
    if (employeeDraft?.forwardLook) return employeeDraft.forwardLook
    return reflection?.forwardLook || {}
  }

  // Sub-goal snapshots for closed state
  const subgoalSnapshots = reflection?.subgoalSnapshotsAtClose

  const saveIndicator = employeeSaveState === 'saving' || managerSaveState === 'saving'
    ? 'Saving\u2026'
    : employeeSaveState === 'saved' || managerSaveState === 'saved'
      ? 'Saved'
      : null

  const annotationProps = {
    isEmployee,
    isManager,
    isShared,
    isReady,
    isClosed,
    employeeName: 'the employee',
  }

  const canSuggest = me?.hasClaudeKey && isManager && isReady && !isClosed

  // Build the report name from reports list
  const reportName = useMemo(() => {
    if (!effectiveOwner || !reports?.length) return 'the employee'
    const r = reports.find((rep) => rep.reportId === effectiveOwner)
    return r?.reportName || 'the employee'
  }, [effectiveOwner, reports])

  const createSuggestHandler = useCallback((sectionKey, cardKey) => {
    if (!canSuggest) return undefined
    return async () => {
      const result = await suggestManagerNote({
        sectionKey,
        cardKey,
        reflection: JSON.stringify({
          successes: getTrackData('successes'),
          challenges: getTrackData('challenges'),
          sideQuests: getTrackData('sideQuests'),
          forwardLook: getForwardLook(),
        }),
        goals: goals ? JSON.stringify(goals) : null,
        recentSessions: null,
        reportName,
      })
      return result?.suggestion || ''
    }
  }, [canSuggest, reflection, employeeDraft, goals, reportName]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={styles.page}>
      {saveIndicator && (
        <div style={styles.topBar}>
          <span style={styles.saveIndicator}>{saveIndicator}</span>
        </div>
      )}

      {loading && !reflection && <p style={styles.muted}>Loading...</p>}

      {reflection && (
        <>
          {/* ── Header ── */}
          <div style={styles.titleSection}>
            <h2 style={styles.title}>{reflection.title}</h2>
            {(reflection.periodStart || reflection.periodEnd) && (
              <span style={styles.period}>
                {reflection.periodStart && formatDate(reflection.periodStart)}
                {reflection.periodStart && reflection.periodEnd && ' \u2013 '}
                {reflection.periodEnd && formatDate(reflection.periodEnd)}
              </span>
            )}
          </div>

          <ReflectionLifecycleBar
            reflection={reflection}
            isManager={isManager}
            isEmployee={isEmployee}
            onMarkReady={handleMarkReady}
            onShare={handleShare}
            onClose={handleClose}
            onReopen={handleReopen}
            hasCurrentManager={hasManager}
          />

          {/* ── Zone 1: Goals reminder ── */}
          <ReflectionGoalsStrip
            ownerUserId={effectiveOwner}
            reflection={reflection}
          />

          {/* ── Zone 2: Reflection tracks ── */}
          <div style={styles.zone}>
            <SuccessesTrack
              data={getTrackData('successes')}
              onChange={(data) => handleTrackChange('successes', data)}
              managerNotes={getManagerNotes('successes')}
              onManagerNoteChange={(notes) => handleManagerNoteChange('successes', notes)}
              subgoals={allSubgoals}
              snapshots={subgoalSnapshots?.successes?.card1}
              createSuggestHandler={createSuggestHandler}
              {...annotationProps}
            />

            <ChallengesTrack
              data={getTrackData('challenges')}
              onChange={(data) => handleTrackChange('challenges', data)}
              managerNotes={getManagerNotes('challenges')}
              onManagerNoteChange={(notes) => handleManagerNoteChange('challenges', notes)}
              subgoals={allSubgoals}
              snapshots={subgoalSnapshots?.challenges?.card1}
              createSuggestHandler={createSuggestHandler}
              {...annotationProps}
            />

            <SideQuestsTrack
              data={getTrackData('sideQuests')}
              onChange={(data) => handleTrackChange('sideQuests', data)}
              managerNotes={getManagerNotes('sideQuests')}
              onManagerNoteChange={(notes) => handleManagerNoteChange('sideQuests', notes)}
              createSuggestHandler={createSuggestHandler}
              {...annotationProps}
            />
          </div>

          {/* ── Zone 3: Forward look ── */}
          <ForwardLookSection
            data={getForwardLook()}
            onChange={handleForwardLookChange}
            managerNotes={getManagerNotes('forwardLook')}
            onManagerNoteChange={(notes) => handleManagerNoteChange('forwardLook', notes)}
            onPromoteToTask={handlePromoteToTask}
            createSuggestHandler={createSuggestHandler}
            me={me}
            reports={reports}
            {...annotationProps}
          />

          {/* ── Zone 4: Private notes ── */}
          {(isEmployee || isManager) && (
            <div style={styles.zone}>
              <ReflectionPrivateNotes
                reflectionId={reflectionId}
                ownerUserId={effectiveOwner}
                side={isManager ? 'MANAGER' : 'EMPLOYEE'}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

const styles = {
  page: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  saveIndicator: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
  },
  titleSection: { display: 'flex', flexDirection: 'column', gap: '4px' },
  title: { fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em', margin: 0 },
  period: { fontSize: '13px', color: 'var(--text-secondary)' },
  zone: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  muted: { fontSize: '13px', color: 'var(--text-secondary)' },
}
